import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, User, Edit, MapPin, CreditCard, Award, Shield, AlertTriangle, Users, HelpCircle, Settings, LogOut, DollarSign, Heart, Gift, Sparkles, Search, Wallet, Wine, Lock } from 'lucide-react';
import NearULogo from '../components/NearULogo';
import MembershipBadge from '../components/MembershipBadge';
import AgeBadge from '../components/AgeBadge';
import { FEATURES } from '../config/featureFlags';
import useTheme from '../hooks/useTheme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { isDark, colors } = useTheme();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  if (!profile) return <div style={{ padding: '20px', color: colors.textPrimary }}>Loading...</div>;

  // Build verification items - Income Verification only for Elite members
  const verificationItems = [
    { icon: Shield, label: 'ID Verification', path: '/profile/verification', testId: 'verification' },
    { icon: Shield, label: 'Background Check', path: '/profile/verification', testId: 'background-check' },
  ];

  // Add Income Verification only for Elite members
  if (profile?.elite) {
    verificationItems.push({
      icon: DollarSign,
      label: 'Income Verification',
      path: '/profile/income-verification',
      testId: 'income-verification',
      eliteOnly: true
    });
  }

  const menuItems = [
    { section: 'account', title: 'My Profile', items: [
      { icon: Edit, label: 'Edit Profile', path: '/profile/edit', testId: 'edit-profile' },
      { icon: User, label: 'Account Information', path: '/profile/account', testId: 'account-info' },
    ]},
    { section: 'membership', title: 'Membership & Billing', items: [
      { icon: Award, label: 'Manage Membership', subtitle: 'Free Plan', path: '/profile/manage-membership', testId: 'manage-membership' },
      { icon: Wallet, label: 'Payment & Wallet', path: '/profile/wallet', testId: 'wallet' },
    ]},
    { section: 'verification', title: 'Verification', items: verificationItems },
    { section: 'privacy', title: 'Privacy & Safety', items: [
      { icon: MapPin, label: 'Privacy Zones', path: '/profile/privacy-zones', testId: 'privacy-zones' },
      { icon: Users, label: 'Blocked Users', path: '/profile/blocked-users', testId: 'blocked-users' },
    ]},
    { section: 'explore', title: 'Explore & Connect', items: [
      { icon: Search, label: 'Browse Members', path: '/swipe?section=browse', testId: 'browse-members' },
      { icon: Heart, label: 'My Favorites', path: '/swipe?section=favorites', testId: 'favorites' },
      { icon: MapPin, label: 'Request a Hotspot', path: '/map', testId: 'hotspot-request' },
    ]},
    { section: 'premium', title: 'Premium Services', items: [
      { icon: Sparkles, label: 'NearU Concierge', path: '/concierge', testId: 'concierge', highlight: true },
      { icon: Gift, label: 'Send Gifts', path: '/gifts', testId: 'gifts', highlight: !FEATURES.gifting, comingSoon: !FEATURES.gifting },
      { icon: Wine, label: 'Send a Drink', path: '/send-drink', testId: 'send-drink', highlight: true },
      { icon: Wine, label: 'Drink History', path: '/profile/drink-history', testId: 'drink-history' },
    ]},
    { section: 'refer', title: 'Invite Friends', items: [
      { icon: Users, label: 'Refer a Friend', path: '/profile/refer', testId: 'refer-friend', highlight: true },
    ]},
    { section: 'other', title: 'Settings & Support', items: [
      { icon: Settings, label: 'Settings', path: '/profile/settings', testId: 'settings' },
      { icon: HelpCircle, label: 'Help & Support', path: '/profile/help', testId: 'help-support' },
    ]},
  ];

  // Theme-aware styling
  const pageColors = {
    background: isDark ? '#0D0D0D' : '#FDFCFA',
    cardBg: isDark ? 'rgba(26, 15, 10, 0.95)' : 'rgba(255, 255, 255, 0.98)',
    headerBg: isDark ? 'rgba(230, 215, 195, 0.05)' : 'rgba(139, 105, 20, 0.04)',
    text: isDark ? '#E6D7C3' : '#1A1410',
    textMuted: isDark ? '#A57C33' : '#4A4035',
    textAccent: isDark ? '#D7B56D' : '#8B6914',
    border: isDark ? '#A57C33' : 'rgba(139, 105, 20, 0.25)',
    gold: isDark ? '#D7B56D' : '#8B6914',
    goldGradient: isDark 
      ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)' 
      : 'linear-gradient(135deg, #A57C33 0%, #8B6914 100%)',
    editBtnBorder: isDark ? '#1A0F0A' : '#FDFCFA',
    menuItemColor: isDark ? '#E6D7C3' : '#1A1410',
    menuItemHighlight: isDark ? '#D7B56D' : '#8B6914',
    menuSubtitle: isDark ? '#A57C33' : '#6D6358',
    sectionTitle: isDark ? '#A57C33' : '#8B6914'
  };

  return (
    <div style={{ background: pageColors.background, minHeight: '100vh' }}>
      <div className="page-header" style={{ background: isDark ? 'var(--bg-secondary)' : '#F5F2ED' }}>
        <div style={{ marginBottom: '12px' }}><NearULogo size="small" showText={true} /></div>
        <h1 className="page-title" style={{ fontFamily: 'Playfair Display, serif' }}>PROFILE</h1>
      </div>

      <div style={{ padding: '28px', textAlign: 'center', background: pageColors.headerBg, borderBottom: `1px solid ${pageColors.border}` }}>
        <div 
          onClick={() => navigate('/profile/edit?tab=photos')}
          style={{ 
            position: 'relative', 
            display: 'inline-block', 
            cursor: 'pointer',
            marginBottom: '16px'
          }}
        >
          <img
            src={profile.photos && profile.photos[0] ? profile.photos[0] : 'https://i.pravatar.cc/150?img=33'}
            alt="Profile"
            style={{ width: '110px', height: '110px', borderRadius: '50%', objectFit: 'cover', border: `3px solid ${pageColors.gold}`, boxShadow: isDark ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)' }}
          />
          <div style={{
            position: 'absolute',
            bottom: '0',
            right: '0',
            background: pageColors.goldGradient,
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `2px solid ${pageColors.editBtnBorder}`
          }}>
            <Edit size={16} color="#2C1B13" />
          </div>
        </div>
        <h2 style={{ fontSize: '26px', fontWeight: 'bold', marginBottom: '6px', color: pageColors.text, fontFamily: 'Playfair Display, serif' }}>
          {profile.name}{profile.show_age && profile.age ? `, ${profile.age}` : ''}
        </h2>
        <p style={{ color: pageColors.textMuted, fontSize: '14px', marginBottom: '10px' }}>
          {profile.location}
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {/* Membership Tier Badge */}
          <MembershipBadge tier="free" size="medium" />
          {/* Age Badge - Shows 21+ indicator for verified users over 21 */}
          {profile.age && (
            <AgeBadge 
              age={profile.age}
              isVerified={profile.identity_verified || profile.verified}
              isUnder21={profile.is_under_21}
              size="medium"
              showAge={!profile.identity_verified}
            />
          )}
          {profile.verified && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: 'rgba(40, 167, 69, 0.2)', color: '#28a745', border: '1px solid #28a745', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>
              <Shield size={14} /> Verified
            </span>
          )}
        </div>
      </div>

      <div style={{ background: pageColors.background }}>
        {menuItems.map((section) => (
          <div key={section.section}>
            <div style={{ padding: '18px 20px 10px', fontSize: '13px', fontWeight: '600', color: pageColors.sectionTitle, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {section.title}
            </div>
            {section.items.map((item) => {
              const Icon = item.icon;
              const isDisabled = item.comingSoon;
              return (
                <button
                  key={item.label}
                  onClick={() => !isDisabled && navigate(item.path)}
                  className="menu-item"
                  data-testid={item.testId}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '18px 20px',
                    color: isDisabled ? (isDark ? '#6B5B4D' : '#9A9489') : (item.highlight ? pageColors.menuItemHighlight : pageColors.menuItemColor),
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.7 : 1,
                    background: isDark ? 'transparent' : 'transparent'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{ position: 'relative' }}>
                      <Icon size={22} />
                      {isDisabled && <Lock size={10} style={{ position: 'absolute', bottom: -2, right: -4, color: pageColors.textMuted }} />}
                    </div>
                    <div>
                      <div style={{ fontWeight: item.highlight ? '600' : 'normal', fontSize: '15px' }}>
                        {item.label}
                        {isDisabled && <span style={{ marginLeft: '8px', fontSize: '10px', padding: '2px 6px', background: isDark ? 'rgba(74, 64, 57, 0.5)' : 'rgba(139, 105, 20, 0.15)', borderRadius: '4px', verticalAlign: 'middle' }}>Coming Soon</span>}
                      </div>
                      {item.subtitle && <div style={{ fontSize: '12px', color: pageColors.menuSubtitle, marginTop: '2px' }}>{item.subtitle}</div>}
                    </div>
                  </div>
                  <ChevronRight size={20} style={{ color: isDisabled ? (isDark ? '#4A4039' : '#BFBAB3') : pageColors.textMuted }} />
                </button>
              );
            })}
          </div>
        ))}

        <button
          onClick={() => {
            localStorage.removeItem('nearU_loggedIn');
            navigate('/login');
          }}
          className="menu-item"
          style={{ color: '#6A1F28', marginTop: '20px' }}
          data-testid="logout-btn"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <LogOut size={22} />
            <span style={{ fontSize: '15px', fontWeight: '500' }}>Logout</span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default ProfilePage;